/**
 * MercadoPago Checkout Pro redirect helpers.
 *
 * MP's /checkout/v1/payment/redirect/ page relies on nonce-based CSP scripts that
 * fail inside Instagram/Meta in-app browsers. Prefer opening the init_point in
 * Safari/Chrome (user gesture via <a> or x-safari-https) instead of location.replace.
 */

import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";
import { isMetaInAppBrowser } from "@/lib/inAppBrowserCheckout";

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

/** Production checkout URL; sandbox only when init_point is absent (local dev). */
export function resolveMercadoPagoCheckoutUrl(
  initPoint?: string | null,
  sandboxInitPoint?: string | null,
): string | null {
  const prod = initPoint?.trim();
  if (prod) return prod;
  const sandbox = sandboxInitPoint?.trim();
  if (sandbox && import.meta.env.DEV) return sandbox;
  return null;
}

/**
 * Navigate to MP via native top-level navigation (no iframe).
 * Uses a transient <a> click first — survives stricter WebViews better than assign().
 */
export function navigateToMercadoPagoCheckout(url: string): void {
  if (typeof window === "undefined" || !url) return;

  const target = window.top ?? window;

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.rel = "noopener noreferrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return;
  } catch {
    /* fall through */
  }

  try {
    target.location.assign(url);
  } catch {
    target.location.href = url;
  }
}

/** Android intent:// URL to open destination in Chrome. */
function buildAndroidChromeIntent(destinationUrl: string): string | null {
  try {
    const u = new URL(destinationUrl);
    const hostPath = `${u.host}${u.pathname}${u.search}`;
    return `intent://${hostPath}#Intent;scheme=https;package=com.android.chrome;end`;
  } catch {
    return null;
  }
}

/**
 * Best-effort escape from Meta in-app browser into the system browser.
 * iOS: x-safari-https / com-apple-mobilesafari-tab schemes, then direct URL.
 * Android: Chrome intent, then direct URL.
 */
export function openMercadoPagoInExternalBrowser(url: string): void {
  if (typeof window === "undefined" || !url) return;

  if (isIos()) {
    const xSafariScheme = `x-safari-https://${url.replace(/^https?:\/\//, "")}`;
    const legacyScheme = `com-apple-mobilesafari-tab:${url}`;

    // Synchronous attempts — Meta IAB only honors escapes tied to the user gesture.
    try {
      const blank = document.createElement("a");
      blank.href = url;
      blank.target = "_blank";
      blank.rel = "noopener noreferrer";
      blank.style.display = "none";
      document.body.appendChild(blank);
      blank.click();
      document.body.removeChild(blank);
    } catch {
      /* fall through */
    }
    try {
      window.location.href = xSafariScheme;
    } catch {
      /* fall through */
    }

    window.setTimeout(() => {
      window.location.href = legacyScheme;
    }, 300);
    window.setTimeout(() => {
      navigateToMercadoPagoCheckout(url);
    }, 1000);
    return;
  }

  if (isAndroid()) {
    const intent = buildAndroidChromeIntent(url);
    if (intent) {
      window.location.href = intent;
      window.setTimeout(() => navigateToMercadoPagoCheckout(url), 800);
      return;
    }
  }

  navigateToMercadoPagoCheckout(url);
}

/** MP Checkout Pro breaks inside Instagram/Meta IAB due to CSP on their redirect page. */
export function shouldUseMercadoPagoInstagramEscape(): boolean {
  return isInstagramBrowser() || isMetaInAppBrowser();
}

/** Hosted MercadoPago checkout / subscription links (not internal app routes). */
export function isMercadoPagoHostedUrl(url: string): boolean {
  if (!url?.trim()) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.includes("mercadopago.com") || host.includes("mpago.la");
  } catch {
    return /mercadopago\.com|mpago\.la/i.test(url);
  }
}

/**
 * @deprecated Prefer `useMercadoPagoInstagramEscape().interceptMercadoPagoCta` so
 * the visitor sees the escape overlay (copy link + instrucciones). Kept for
 * call-sites that cannot mount the overlay hook yet.
 */
export function interceptMercadoPagoCtaForInstagram(
  url: string,
  onNavigate?: () => void,
): boolean {
  if (!isMercadoPagoHostedUrl(url) || !shouldUseMercadoPagoInstagramEscape()) {
    return false;
  }
  onNavigate?.();
  openMercadoPagoInExternalBrowser(url);
  return true;
}